import graphql__d74a780888b5979c98c5 from "./__generated__/ContactActivityView_contact.graphql.ts";
import graphql__3104d18b28338c6d60a8 from "./__generated__/ContactActivityViewQuery.graphql.ts";
import graphql__1612da5079f13e1d3558 from "./__generated__/ContactActivityViewRefreshQuery.graphql.ts";
import graphql__3af2fd095c19af287c75 from "./__generated__/ContactActivityViewLoadMoreQuery.graphql.ts";import * as React from "react";
import {useState, useCallback} from "react";
import {graphql, useLazyLoadQuery, fetchQuery, useRelayEnvironment} from "react-relay";
import Panel from "./presentational/Panel";
import ActivityTimeline from "./ActivityTimeline";
import LoadMoreButton from "./presentational/LoadMoreButton";
import {useFragment} from "react-relay/hooks";
import {useOutletContext} from "react-router";
import {useIntl} from "react-intl";
import {ContactActivityViewQuery} from "./__generated__/ContactActivityViewQuery.graphql";
import {useActivityStream} from "../hooks/useActivityStream";

type Props = {}

type Activity = {
    readonly id: string;
    readonly source: string;
    readonly type: string;
    readonly occurredAt: string;
    readonly metadata: any;
    readonly actorName: string | null;
    readonly body: string | null;
};

const PAGE_SIZE = 50;

const ContactActivityView: React.FC<Props> = () => {
    const queryRef: any = useOutletContext();
    const intl = useIntl();
    const environment = useRelayEnvironment();

    const contact = useFragment(
        graphql__d74a780888b5979c98c5,
        queryRef
    );

    const initialData = useLazyLoadQuery<ContactActivityViewQuery>(
        graphql__3104d18b28338c6d60a8,
        {contactIdentifier: contact.contactIdentifier, first: PAGE_SIZE, offset: 0}
    );

    const [activities, setActivities] = useState<ReadonlyArray<Activity>>(
        initialData.contactActivities?.activities || []
    );
    const [hasMore, setHasMore] = useState(initialData.contactActivities?.hasMore || false);
    const [isLoadingMore, setIsLoadingMore] = useState(false);

    // Subscribe to realtime activity updates via SSE
    const handleNewActivity = useCallback(() => {
        // Refetch the first page to get the new activity
        fetchQuery(
            environment,
            graphql__1612da5079f13e1d3558,
            {contactIdentifier: contact.contactIdentifier, first: PAGE_SIZE, offset: 0}
        ).subscribe({
            next: (data: any) => {
                const newActivities = data.contactActivities?.activities || [];
                setActivities(newActivities);
                setHasMore(data.contactActivities?.hasMore || false);
            }
        });
    }, [environment, contact.contactIdentifier]);

    useActivityStream({
        subjectType: 'contact',
        subjectIdentifier: contact.contactIdentifier,
        onActivity: handleNewActivity,
    });

    const loadMore = useCallback(() => {
        if (isLoadingMore || !hasMore) return;

        setIsLoadingMore(true);
        const newOffset = activities.length;

        fetchQuery(
            environment,
            graphql__3af2fd095c19af287c75,
            {contactIdentifier: contact.contactIdentifier, first: PAGE_SIZE, offset: newOffset}
        ).subscribe({
            next: (data: any) => {
                const newActivities = data.contactActivities?.activities || [];
                setActivities(prev => [...prev, ...newActivities]);
                setHasMore(data.contactActivities?.hasMore || false);
                setIsLoadingMore(false);
            },
            error: () => {
                setIsLoadingMore(false);
            }
        });
    }, [environment, contact.contactIdentifier, activities.length, hasMore, isLoadingMore]);

    return (
        <Panel headline={intl.formatMessage({id: 'tab.activities', defaultMessage: 'Activities'})}>
            <ActivityTimeline activities={activities} />
            {hasMore && (
                <LoadMoreButton onClick={loadMore} isLoadingNext={isLoadingMore} />
            )}
        </Panel>
    );
}

export default ContactActivityView;
